<script setup lang="ts">
import QRCode from 'qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { isGobyInstalled } from '~/utilities/walletAdapter/gobyAdapter';
import { walletConnectAdapter } from '~/utilities/walletAdapter/walletConnectAdapter';
import { walletModalEmitter } from '~/utilities/walletAdapter/walletModalEmitter';

const show = ref(false);
const showQRModal = ref(false);
const showWaitingModal = ref(false);

const gobyStore = useGobyStore();

// QR Modal state
const qrUri = ref('');
const qrCodeDataUrl = ref<string>('');

// Waiting Modal state
const waitingModalRequiresConfirmation = ref(false);

// Copy functionality
const copied = ref(false);

console.log('gobyStore', gobyStore);

// Generate QR code when URI changes
watch(
  () => qrUri.value,
  async (newUri) => {
    if (newUri) {
      try {
        qrCodeDataUrl.value = await QRCode.toDataURL(newUri, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  },
  { immediate: true }
);

const copyToClipboard = async () => {
  try {
    // Try the modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(qrUri.value);
    } else {
      // Fallback for iOS Safari
      const textArea = document.createElement('textarea');
      textArea.value = qrUri.value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
    }
    copied.value = true;
    setTimeout(() => (copied.value = false), 3000);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
  }
};

// Event handlers
const handleShowQRModal = (event: any) => {
  qrUri.value = event.detail;
  showQRModal.value = true;
};

const handleHideQRModal = () => {
  showQRModal.value = false;
};

const handleShowWaitingModal = (event: any) => {
  showWaitingModal.value = true;
  waitingModalRequiresConfirmation.value = event.detail?.requiresConfirmation || false;
};

const handleHideWaitingModal = () => {
  showWaitingModal.value = false;
};

// Wallet modal event handlers
const handleShowWalletModal = () => {
  show.value = true;
};

const handleHideWalletModal = () => {
  show.value = false;
};

// Reset states when modals close
watch(showQRModal, (newValue) => {
  if (!newValue) {
    qrUri.value = '';
    qrCodeDataUrl.value = '';
    copied.value = false;
  }
});

watch(showWaitingModal, (newValue) => {
  if (!newValue) {
    waitingModalRequiresConfirmation.value = false;
  }
});

console.log('walletConnectAdapter', walletConnectAdapter);

onMounted(() => {
  walletConnectAdapter.modalEmitter.addEventListener('showQRModal', handleShowQRModal);
  walletConnectAdapter.modalEmitter.addEventListener('hideQRModal', handleHideQRModal);
  walletConnectAdapter.modalEmitter.addEventListener('showWaitingModal', handleShowWaitingModal);
  walletConnectAdapter.modalEmitter.addEventListener('hideWaitingModal', handleHideWaitingModal);

  // Listen for wallet modal events
  walletModalEmitter.addEventListener('showWalletModal', handleShowWalletModal);
  walletModalEmitter.addEventListener('hideWalletModal', handleHideWalletModal);
});

onUnmounted(() => {
  walletConnectAdapter.modalEmitter.removeEventListener('showQRModal', handleShowQRModal);
  walletConnectAdapter.modalEmitter.removeEventListener('hideQRModal', handleHideQRModal);
  walletConnectAdapter.modalEmitter.removeEventListener('showWaitingModal', handleShowWaitingModal);
  walletConnectAdapter.modalEmitter.removeEventListener('hideWaitingModal', handleHideWaitingModal);

  // Remove wallet modal event listeners
  walletModalEmitter.removeEventListener('showWalletModal', handleShowWalletModal);
  walletModalEmitter.removeEventListener('hideWalletModal', handleHideWalletModal);
});

const navTo = (path: string) => {
  navigateTo(path);
  show.value = false;
};

const connectWallet = () => {
  console.log('connectWallet');
  gobyStore.connect('walletconnect');
};
</script>
<template>
  <!-- Wallet Selection Dialog -->
  <Dialog v-model:open="show">
    <DialogContent class="w-full max-w-md">
      <DialogHeader>
        <DialogTitle class="text-xl font-semibold leading-6 text-neutral-900 dark:text-neutral-50">
          Connect your Wallet
        </DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-4">
        <button
          v-if="isGobyInstalled()"
          class="shadow-attribute hover:shadow-card-hover duration-50 flex w-full justify-between rounded-lg px-6 py-4 font-bold text-neutral-700 transition ease-in-out dark:text-neutral-200"
          @click="gobyStore.connect('goby')"
        >
          <span class="flex gap-4"> <img :class="[' h-6 w-6']" aria-hidden="true" src="/goby-logo.svg" />Goby </span>
          <span
            :class="[
              gobyStore.type === 'goby' && gobyStore.account
                ? 'text-green-600'
                : 'text-neutral-600 dark:text-neutral-300',
            ]"
            >{{ gobyStore.type === 'goby' && gobyStore.account ? 'Connected' : 'Connect' }}</span
          >
        </button>
        <NuxtLink
          v-else
          to="https://www.goby.app"
          target="_blank"
          class="shadow-attribute hover:shadow-card-hover duration-50 flex w-full justify-between rounded-lg px-6 py-4 font-bold text-neutral-700 transition ease-in-out dark:text-neutral-200"
        >
          <span class="flex gap-4"> <img :class="[' h-6 w-6']" aria-hidden="true" src="/goby-logo.svg" />Goby </span>
          <span :class="[gobyStore.account ? 'text-green-600' : 'text-neutral-600 dark:text-neutral-300']"
            >Install</span
          >
        </NuxtLink>
        <button
          class="shadow-attribute z-100 hover:shadow-card-hover duration-50 pointer-events-auto flex w-full justify-between rounded-lg px-6 py-4 font-bold text-neutral-700 transition ease-in-out dark:text-neutral-200"
          @click="connectWallet()"
        >
          <span class="flex gap-4">
            <img :class="[' h-6 w-6']" aria-hidden="true" src="https://sagewallet.net/assets/sage-logo.png" />Sage
          </span>
          <span
            :class="[
              gobyStore.type === 'walletconnect' && gobyStore.account
                ? 'text-green-600'
                : 'text-neutral-600 dark:text-neutral-300',
            ]"
            >{{ gobyStore.type === 'walletconnect' && gobyStore.account ? 'Connected' : 'Connect' }}</span
          >
        </button>
      </div>

      <div class="mt-6 text-center">
        <NuxtLink
          to="/login"
          class="text-sm text-neutral-600 hover:text-neutral-700 dark:text-neutral-200 dark:hover:text-neutral-300"
          @click="navTo('/login')"
          >Legacy Login</NuxtLink
        >
      </div>
    </DialogContent>
  </Dialog>

  <!-- QR Code Dialog -->
  <Dialog v-model:open="showQRModal">
    <DialogContent class="w-full max-w-md">
      <DialogHeader>
        <DialogTitle class="text-xl font-semibold leading-6 text-neutral-900 dark:text-neutral-50">
          Connect with Wallet
        </DialogTitle>
      </DialogHeader>

      <div class="flex flex-col items-center gap-6 text-center">
        <!-- QR Code -->
        <div v-if="qrCodeDataUrl" class="rounded-lg bg-white p-4">
          <img :src="qrCodeDataUrl" alt="WalletConnect QR Code" class="h-64 w-64" />
        </div>

        <div v-else class="flex h-64 w-64 items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-800">
          <span class="text-neutral-500">Generating QR code...</span>
        </div>

        <!-- Copy Link Button -->
        <button
          class="w-full rounded-lg bg-emerald-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
          @click="copyToClipboard"
        >
          {{ copied ? 'Copied!' : 'Copy Link' }}
        </button>

        <p class="text-center text-sm text-neutral-600 dark:text-neutral-400">
          Scan this QR code with your wallet app or copy the link to connect
        </p>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Waiting Dialog -->
  <Dialog v-model:open="showWaitingModal">
    <DialogContent class="w-full max-w-md">
      <DialogHeader>
        <DialogTitle class="text-xl font-semibold leading-6 text-neutral-900 dark:text-neutral-50">
          Waiting for Wallet
        </DialogTitle>
      </DialogHeader>

      <div class="flex flex-col items-center gap-6 text-center">
        <!-- Loading Spinner -->
        <div class="flex h-32 w-32 items-center justify-center">
          <div class="relative">
            <div class="h-16 w-16 rounded-full border-4 border-neutral-200 dark:border-neutral-700"></div>
            <div
              class="absolute top-0 h-16 w-16 animate-spin rounded-full border-4 border-transparent border-t-emerald-600"
            ></div>
          </div>
        </div>

        <div class="text-center">
          <p class="mb-2 text-lg font-medium text-neutral-900 dark:text-neutral-50">Waiting for wallet response...</p>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">
            <template v-if="waitingModalRequiresConfirmation">
              Please switch to your wallet app and approve the request
            </template>
            <template v-else> Please switch to your wallet app for a few seconds to complete the connection </template>
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
